import React, { useEffect } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useLocation } from "react-router-dom";
import { SITE_BG } from "../../../dark-theme/palette";
import { trackEvent } from "../../../services/GoogleAnalytics";
import Item from "../../components/Item";
import BannedWordsSection from "../../login/BannedWordsSection";
import { usePagesTranslation } from "../../locales";

const primaryTitleSx = { fontWeight: 600, color: "#1976d2" };
const sectionTitleSx = { mb: 2, fontWeight: 600, color: "text.primary" };
const bodySx = { fontSize: "1.1rem", lineHeight: 1.8 };

export default function HowToBannedWords() {
    const { t } = usePagesTranslation();
    const location = useLocation();

    useEffect(() => {
        trackEvent("entered", { name: "HowToBannedWords" });
    }, []);

    useEffect(() => {
        const raw = location.hash.replace(/^#/, "");
        if (!raw) return;
        const id = window.setTimeout(() => {
            document.getElementById(raw)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
        return () => window.clearTimeout(id);
    }, [location.pathname, location.hash]);

    return (
        <Box sx={{ backgroundColor: SITE_BG, minHeight: "100vh", width: "100%", overflowX: "hidden" }}>
            <Box sx={{ width: "100%", maxWidth: "100%", margin: 0 }}>
                <Item
                    sx={{
                        p: { xs: 2, md: 4 },
                        textAlign: "left",
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                    }}
                >
                    <Typography variant="h3" component="h1" sx={{ mb: 4, ...primaryTitleSx }}>
                        {t("howto.banned.page-title")}
                    </Typography>

                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" sx={sectionTitleSx}>
                            {t("howto.banned.intro-title")}
                        </Typography>
                        <Typography variant="body1" sx={{ ...bodySx, mb: 0 }}>
                            {t("howto.banned.intro-p")}
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box
                        id="difficulty"
                        component="section"
                        sx={{
                            mb: 5,
                            width: "100%",
                            maxWidth: "100%",
                        }}
                    >
                        <Typography variant="h5" component="h2" sx={sectionTitleSx}>
                            {t("landing.difficulty-title")}
                        </Typography>
                        <Alert
                            severity="info"
                            sx={{
                                width: "100%",
                                maxWidth: "100%",
                                boxSizing: "border-box",
                            }}
                        >
                            <Typography variant="body1" sx={{ ...bodySx, mb: 2, fontWeight: 500 }}>
                                {t("landing.difficulty-intro")}
                            </Typography>
                            <Box sx={{ pl: { xs: 0, sm: 2 } }}>
                                <Typography variant="body1" sx={{ ...bodySx, mb: 1.5 }}>
                                    <Box component="strong" sx={{ color: "#4caf50" }}>
                                        {t("puzzle.card.difficulty-easy")}:
                                    </Box>{" "}
                                    {t("landing.difficulty-easy")}
                                </Typography>
                                <Typography variant="body1" sx={{ ...bodySx, mb: 1.5 }}>
                                    <Box component="strong" sx={{ color: "#ff9800" }}>
                                        {t("puzzle.card.difficulty-medium")}:
                                    </Box>{" "}
                                    {t("landing.difficulty-medium")}
                                </Typography>
                                <Typography variant="body1" sx={bodySx}>
                                    <Box component="strong" sx={{ color: "#f44336" }}>
                                        {t("puzzle.card.difficulty-hard")}:
                                    </Box>{" "}
                                    {t("landing.difficulty-hard")}
                                </Typography>
                            </Box>
                        </Alert>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box id="banned-words-manager" sx={{ mb: 5, width: "100%", maxWidth: "100%" }}>
                        <Typography variant="h5" component="h2" sx={sectionTitleSx}>
                            {t("howto.banned.title")}
                        </Typography>
                        <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600, color: "text.secondary" }}>
                            {t("howto.banned.categories-title")}
                        </Typography>
                        <BannedWordsSection embedded sx={{ mt: 0, mb: 0 }} />
                    </Box>
                </Item>
            </Box>
        </Box>
    );
}
